import { motion } from 'motion/react'
import { Coffee, PenLine, Luggage } from 'lucide-react'
import { CALENDLY_URL } from './constants'
import { useLanguage } from '@/i18n/LanguageContext'

const STEP_META = [
  { number: '1', icon: Coffee },
  { number: '2', icon: PenLine },
  { number: '3', icon: Luggage },
]

function RouteLine() {
  return (
    <svg
      className="pointer-events-none absolute left-0 top-8 hidden h-40 w-full -translate-y-1/2 text-terracotta/50 md:block"
      viewBox="0 0 1200 160"
      fill="none"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path
        d="M20 80 C 180 20, 320 140, 480 80 S 780 20, 940 90 S 1140 120, 1180 70"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeDasharray="10 12"
        strokeLinecap="round"
      />
      <circle cx="20" cy="80" r="5" fill="currentColor" />
      <g
        transform="translate(1180 70) scale(1.2) translate(-12 -12)"
        fill="currentColor"
      >
        <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
      </g>
    </svg>
  )
}

export default function ProcessSection() {
  const { t } = useLanguage()

  const steps = t.process.steps.map((step, i) => ({ ...step, ...STEP_META[i] }))

  return (
    <section id="come-funziona" className="relative bg-cream pt-12 pb-24 md:pt-16 md:pb-32 overflow-hidden">
      <div className="mx-auto max-w-6xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7 }}
          className="text-center"
        >
          <p className="text-sm uppercase tracking-[0.25em] text-terracotta font-medium mb-4">
            {t.process.eyebrow}
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-ink leading-tight">
            {t.process.titleBefore}{' '}
            <span className="italic text-terracotta">{t.process.titleAccent}</span>
          </h2>
        </motion.div>

        <div className="relative mt-16 md:mt-24">
          <RouteLine />
          <div className="relative grid gap-12 md:grid-cols-3 md:gap-8">
            {steps.map((step, i) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                className="relative flex flex-col items-center text-center md:items-start md:text-left"
              >
                <div className="relative">
                  <motion.span
                    whileHover={{ scale: 1.08 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                    className="flex h-16 w-16 items-center justify-center rounded-full bg-terracotta text-cream shadow-lg shadow-terracotta/30"
                  >
                    <span className="font-display text-2xl">{step.number}</span>
                  </motion.span>
                  <span className="absolute -right-2 -bottom-2 flex h-9 w-9 items-center justify-center rounded-full bg-white text-ink shadow-md border border-ink/5">
                    <step.icon className="h-4 w-4" strokeWidth={1.8} />
                  </span>
                </div>
                <h3 className="mt-6 font-display text-2xl text-ink">{step.title}</h3>
                <p className="mt-3 max-w-xs text-ink/70 leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-16 text-center"
        >
          <motion.a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 300, damping: 18 }}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-terracotta px-8 py-4 font-medium text-cream shadow-lg shadow-terracotta/30 hover:bg-terracotta/90"
          >
            {t.common.ctaLabel}
          </motion.a>
        </motion.div>
      </div>
    </section>
  )
}
